import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, GripVertical, Plus, Trash2 } from 'lucide-react';
import { formatRateHuman, type GstContext } from '@fint/money';
import { api, ApiError } from '@/lib/api';

import { useActiveOrg } from '@/stores/auth';
import { PageHeader } from '@/app/DashboardLayout';
import { activeOnly } from '@/features/common/Drafts';
import {
  IncomeCategorySelect,
  ProjectSelect,
  TagPicker,
  type TagOption,
} from '@/features/common/Classify';
import { Button, Card, CardContent, Input, Money, Textarea } from '@/features/common/ui';
import { HsnSacField } from '@/features/common/HsnSacField';
import { CurrencySelect, RateField, useCurrencyRate } from '@/features/common/CurrencyRate';
import { CustomerSheet, type CustomerRecord } from '@/features/customers/CustomerSheet';
import { useInvoiceTotals, type EditorLine } from './useInvoiceTotals';
import { DOCUMENT_KINDS, type DocumentKind } from './documentKind';
import { useGstEnabled, useToday } from '@/hooks/useOrgSettings';
import { useIsPhone } from '@/hooks/useIsPhone';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/cn';

/** The picker's last option: not a customer, a way to add one. */
const NEW_CUSTOMER = '__new__';

interface CustomerOption {
  id: string;
  displayName: string;
  currency: string | null;
  gstTreatment: string | null;
  placeOfSupply?: string | null;
}
interface ItemOption {
  id: string;
  name: string;
  rate: string | null;
  unit: string | null;
  taxId: string | null;
  hsnSac: string | null;
  description: string | null;
}
interface TaxOption { id: string; name: string; percentage: string }
/**
 * Something billed before by hand, offered for reuse.
 *
 * Deliberately NOT an item: picking one fills the line and leaves `itemId`
 * null, exactly as typing it again would. Promoting it to the catalogue is a
 * separate, explicit decision made on the Items screen - see `recentlyBilled`
 * in items.service.ts for why nothing is created here.
 */
interface BilledOption {
  name: string;
  rate: string;
  unit: string | null;
  taxId: string | null;
  hsnSac: string | null;
}

/** `billed:` namespaces the option value so it cannot collide with an item id. */
const BILLED_PREFIX = 'billed:';

let lineCounter = 0;
const blankLine = (): EditorLine => ({
  // Client-generated, so a line survives reordering and removal before the
  // first save - React keys and the tax engine both need stable identity.
  id: `line-${++lineCounter}-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  quantity: 1,
  rate: '',
  taxId: null,
});

/**
 * The document composer.
 *
 * The single most-used screen in the product, and the one place a mistake costs
 * money. Three properties it has to have:
 *
 *   1. The totals update as you type, using the SAME function the server runs
 *      (see useInvoiceTotals). A figure that changes on Save is a figure nobody
 *      trusts.
 *   2. Nothing computed here is sent. The request carries quantities, rates and
 *      discounts; the server recomputes and stores its own answer.
 *   3. A proforma previews the SAME figures as the invoice it becomes. Which is
 *      why this takes a `kind` rather than being forked - see documentKind.ts.
 */
/** The draft as the detail route returns it - the fields the composer refills. */
interface ExistingDraft {
  id: string;
  number: string;
  status: string;
  partyId: string;
  customer: { id: string; displayName: string } | null;
  date: string;
  dueDate: string | null;
  validUntil?: string | null;
  referenceNumber: string | null;
  notes: string | null;
  terms: string | null;
  projectId: string | null;
  incomeCategoryId: string | null;
  tags?: TagOption[];
  imported?: boolean | number | null;
  /** 1 while `number` is a DRAFT- placeholder; the invoice number is taken at issue. */
  provisionalNumber?: number | null;
  externalNumber?: string | null;
  currency?: string;
  exchangeRate?: string | null;
  exchangeRateSource?: string | null;
  lines: Array<{
    id: string;
    itemId: string | null;
    name: string;
    description: string | null;
    quantity: string;
    unitRate: string;
    unit: string | null;
    hsnSac: string | null;
    taxId: string | null;
  }>;
}

export function DocumentComposer({ kind = 'invoice', editId }: { kind?: DocumentKind; editId?: string }) {
  const config = DOCUMENT_KINDS[kind];
  const org = useActiveOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = useToday();

  const [customerId, setCustomerId] = useState('');
  /**
   * Null until the user picks one, so the field can follow the org's today
   * once settings arrive. Seeding state with a date instead would freeze
   * whatever the browser's timezone said on first render - which for an
   * accountant working on a client in another country is the wrong day.
   */
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  const date = pickedDate ?? today;
  /** Due date on an invoice, valid-until on a proforma. See documentKind.ts. */
  const [secondDate, setSecondDate] = useState('');
  const [reference, setReference] = useState('');
  const [projectId, setProjectId] = useState('');
  const [incomeCategoryId, setIncomeCategoryId] = useState('');
  const [tags, setTags] = useState<TagOption[]>([]);
  // See `TagPicker.onBusyChange`: issuing while a tag is still being created
  // writes the set without it, on a document somebody is about to send.
  const [tagsBusy, setTagsBusy] = useState(false);
  /**
   * Null until typed in, so the field can show the book's default notes and
   * terms (Settings → Letterhead) once they arrive - the same shape as
   * `pickedDate`. The server applies the same defaults to a document that
   * omits them; showing them here is what lets a person change them for
   * this one document, or clear them, before it is issued.
   */
  const [typedNotes, setTypedNotes] = useState<string | null>(null);
  const [typedTerms, setTypedTerms] = useState<string | null>(null);
  const [lines, setLines] = useState<EditorLine[]>([blankLine()]);
  /**
   * The document's currency: null follows the customer (their currency,
   * else the book's) - which is what the server resolves anyway; the picker
   * makes it visible and overridable per document. A UK client invoiced in
   * pounds and, once, in rupees for a domestic job is the case.
   */
  const [pickedCurrency, setPickedCurrency] = useState<string | null>(null);
  // "Add a new customer…" in the picker opens the customer drawer over the
  // composer; the new customer is picked when it closes.
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(false);
  /**
   * ── EDITING AN ISSUED INVOICE ──
   * Allowed, for someone with invoice.manage, and never silent: the reason
   * typed here goes on the audit event beside what changed, field by field,
   * line by line. A wrong rate, a missed line, a GSTIN that arrived after
   * the invoice went out - each is a sentence a CA can read later.
   */
  const [reason, setReason] = useState('');
  const gstEnabled = useGstEnabled();
  const isPhone = useIsPhone();
  const perms = usePermissions();

  /**
   * ── EDITING A DRAFT ──
   * The same composer, refilled from the draft, saving with PATCH. A draft
   * the AI agent proposed can carry a line it read wrong - £1,200 typed as
   * ₹1,200 - and the agent cannot change an amount once proposed; the
   * person can, here, before confirming it. Sent invoices are not edited
   * here: restating a figure a customer holds is its own act.
   */
  const existing = useQuery({
    queryKey: [config.path === 'invoices' ? 'invoice' : 'proforma', org?.id, editId],
    queryFn: () => api.get<ExistingDraft>(`/${config.path}/${editId}`),
    enabled: !!org?.id && !!editId,
  });
  const [refilled, setRefilled] = useState<string | null>(null);
  useEffect(() => {
    const draft = existing.data;
    if (!draft || refilled === draft.id) return;
    setRefilled(draft.id);
    setCustomerId(draft.partyId);
    setPickedDate(draft.date);
    setSecondDate((config.secondDate.field === 'dueDate' ? draft.dueDate : draft.validUntil) ?? '');
    setReference(draft.referenceNumber ?? '');
    setProjectId(draft.projectId ?? '');
    setIncomeCategoryId(draft.incomeCategoryId ?? '');
    setTags(draft.tags ?? []);
    setTypedNotes(draft.notes ?? '');
    setTypedTerms(draft.terms ?? '');
    if (draft.currency) setPickedCurrency(draft.currency);
    setLines(
      draft.lines.length
        ? draft.lines.map((line) => ({
            id: line.id,
            itemId: line.itemId,
            name: line.name,
            description: line.description ?? undefined,
            quantity: Number(line.quantity),
            rate: line.unitRate,
            unit: line.unit ?? undefined,
            hsnSac: line.hsnSac ?? undefined,
            taxId: line.taxId,
          }))
        : [blankLine()],
    );
  }, [existing.data, refilled, config.secondDate.field]);
  const editingDraft = !!editId && existing.data?.status === 'draft';
  const editingIssued = !!editId && !!existing.data && existing.data.status !== 'draft' && existing.data.status !== 'void' && existing.data.status !== 'written_off';

  const customers = useQuery({
    queryKey: ['customers', org?.id, 'picker'],
    queryFn: () => api.get<{ items: CustomerOption[] }>('/customers?status=active&limit=200').then(activeOnly),
    enabled: !!org?.id,
  });
  const items = useQuery({
    queryKey: ['items', org?.id, 'picker'],
    queryFn: () => api.get<{ items: ItemOption[] }>('/items?status=active&limit=200').then(activeOnly),
    enabled: !!org?.id,
  });

  /**
   * The other half of the picker: what this business has billed free-hand and
   * never saved. Without it, an org that has never opened the Items screen has
   * an empty picker and retypes every line from memory - which is how the same
   * service ends up billed at three prices under two HSN codes.
   */
  const billed = useQuery({
    queryKey: ['recently-billed', org?.id],
    queryFn: () => api.get<{ items: BilledOption[] }>('/items/recently-billed'),
    enabled: !!org?.id,
  });
  const taxes = useQuery({
    queryKey: ['taxes', org?.id],
    queryFn: () => api.get<{ items: TaxOption[] }>('/taxes'),
    enabled: !!org?.id,
  });
  const settings = useQuery({
    queryKey: ['settings', org?.id],
    queryFn: () =>
      api.get<{
        baseCurrency: string;
        tax?: { regime: string; stateCode?: string; composition: boolean; defaultTaxMode: 'exclusive' | 'inclusive' };
        defaultNotes?: string;
        defaultTerms?: string;
      }>('/settings'),
    enabled: !!org?.id,
  });
  const notes = typedNotes ?? settings.data?.defaultNotes ?? '';
  const terms = typedTerms ?? settings.data?.defaultTerms ?? '';
  const nextNumber = useQuery({
    queryKey: [`${config.path}-next-number`, org?.id],
    queryFn: () => api.get<{ number?: string }>(`/${config.path}/next-number`),
    enabled: !!org?.id,
  });

  const customer = customers.data?.items.find((c) => c.id === customerId);
  // An imported draft's customer may itself be a draft, absent from the
  // active list; the select still has to show who the invoice is to.
  const draftCustomer =
    editId && existing.data?.customer && !customer && existing.data.customer.id === customerId ? existing.data.customer : null;
  /**
   * An export's customer, with the address the picker's list does not carry.
   *
   * Fetched only for an overseas customer, and only to WARN: CGST Rule 46
   * wants the recipient's address and the country of destination on an
   * export invoice, and an accountant sent one without them cannot file it.
   * It never blocks the save - a person invoicing today with the address
   * coming tomorrow is entitled to - and the review's
   * export_address_missing is the backstop for one that went out anyway.
   * Same key as the customer sheet, so editing the customer from here
   * refreshes the warning.
   */
  const exportCustomer = useQuery({
    queryKey: ['customer', org?.id, customerId],
    queryFn: () => api.get<CustomerRecord>(`/customers/${customerId}`),
    enabled: !!org?.id && !!customerId && customer?.gstTreatment === 'overseas',
  });
  // The country the server prints is the shipping address's, else the billing one's.
  const exportAddressMissing =
    customer?.gstTreatment === 'overseas' &&
    !!exportCustomer.data &&
    !(exportCustomer.data.shippingAddress?.country?.trim() || exportCustomer.data.billingAddress?.country?.trim());
  const baseCurrency = settings.data?.baseCurrency ?? 'INR';
  const currency = pickedCurrency ?? customer?.currency ?? baseCurrency;
  const taxMode = settings.data?.tax?.defaultTaxMode ?? 'exclusive';
  /**
   * The rate, when the document is not in the book's currency: prefilled
   * for the date from the book's rate table and the published feeds, typed
   * over freely, sent with the document and shown beside the total as
   * "≈ ₹1,27,500.00 at 127.5000" - computed with the same `toBase` the
   * server uses, so the preview and the saved figure agree to the paise.
   */
  const fx = useCurrencyRate({
    date,
    currency,
    base: baseCurrency,
    initial: existing.data?.exchangeRate && existing.data.currency === currency ? { rate: existing.data.exchangeRate, source: existing.data.exchangeRateSource ?? null } : null,
  });

  // Mirrors the server's own rule exactly: no context when GST is off, not yet
  // configured, or the org is a composition dealer.
  const gst: GstContext | undefined = useMemo(() => {
    const tax = settings.data?.tax;
    if (tax?.regime !== 'gst' || !tax.stateCode || tax.composition || !customer) return undefined;
    return {
      supplierStateCode: tax.stateCode,
      placeOfSupply: customer.placeOfSupply ?? tax.stateCode,
      treatment: (customer.gstTreatment as GstContext['treatment']) ?? 'consumer',
    };
  }, [settings.data, customer]);

  const totals = useInvoiceTotals({
    lines,
    currency,
    taxMode,
    taxes: taxes.data?.items ?? [],
    gst,
  });

  const save = useMutation({
    mutationFn: (markAsSent: boolean) =>
      (editId ? api.patch<{ id: string }> : api.post<{ id: string }>)(editId ? `/${config.path}/${editId}` : `/${config.path}`, {
        customerId,
        date,
        [config.secondDate.field]: secondDate || undefined,
        referenceNumber: reference || undefined,
        currency,
        // The rate on screen, and where it came from; nothing when the
        // document is in the book's currency.
        exchangeRate: fx.foreign && fx.parsed ? fx.parsed : undefined,
        exchangeRateSource: fx.foreign && fx.parsed ? fx.source : undefined,
        // Sent as typed, even when empty: a person who cleared the pre-filled
        // terms meant this document to have none. Omitted only when nothing
        // was typed AND the defaults have not arrived yet - an omitted field
        // is one the server fills from the book's defaults itself.
        notes: typedNotes ?? settings.data?.defaultNotes,
        terms: typedTerms ?? settings.data?.defaultTerms,
        // An edit saves the draft as a draft; issuing stays where it was -
        // the invoice page, or the Inbox for an imported one.
        markAsSent: editId ? undefined : markAsSent,
        // Why an issued invoice is being changed - required by the server on one.
        reason: editingIssued && reason.trim() ? reason.trim() : undefined,
        // Both are `null` when unset rather than omitted: on an update they are
        // how a classification is taken OFF, and sending the same shape on
        // create keeps one code path.
        projectId: projectId || null,
        incomeCategoryId: incomeCategoryId || null,
        tagIds: tags.length > 0 ? tags.map((tag) => tag.id) : undefined,
        // Quantities, rates and discounts ONLY. No totals: the server computes
        // and stores its own, and a client-supplied one would be refused.
        lines: lines
          .filter((l) => l.name.trim() && l.rate !== '')
          .map((line, index) => ({
            id: line.id,
            itemId: line.itemId ?? null,
            name: line.name,
            description: line.description || undefined,
            quantity: line.quantity,
            rate: line.rate,
            unit: line.unit || undefined,
            hsnSac: line.hsnSac || undefined,
            taxId: line.taxId ?? null,
            sortOrder: index,
          })),
      }),
    onSuccess: (created) => {
      for (const key of config.invalidates) {
        void queryClient.invalidateQueries({ queryKey: [key, org?.id] });
      }
      if (editId) {
        void queryClient.invalidateQueries({ queryKey: ['invoice', org?.id, editId] });
        void queryClient.invalidateQueries({ queryKey: ['invoice-document', org?.id, editId] });
        void queryClient.invalidateQueries({ queryKey: ['inbox'] });
        // The invoice's history panel reads the audit trail this edit just wrote to.
        void queryClient.invalidateQueries({ queryKey: ['audit', org?.id] });
      }
      navigate(`/${config.path}/${editId ?? created.id}`);
    },
  });

  const error = save.error instanceof ApiError ? save.error : undefined;

  const updateLine = (id: string, patch: Partial<EditorLine>) =>
    setLines((current) => current.map((line) => (line.id === id ? { ...line, ...patch } : line)));

  /**
   * Picking fills the line - and every field stays editable afterwards.
   *
   * Two sources, one control. A catalogue item sets `itemId`, which is what
   * makes "sales by item" work. A previously-billed line sets nothing but the
   * values: it is a shortcut for typing, not a claim that the line came from
   * the catalogue, and recording it as one would put a figure in a report
   * against an item that does not exist.
   */
  const applyPick = (lineId: string, value: string) => {
    if (value.startsWith(BILLED_PREFIX)) {
      const pick = billed.data?.items.find((b) => b.name === value.slice(BILLED_PREFIX.length));
      if (!pick) return;
      updateLine(lineId, {
        itemId: null,
        pickedName: pick.name,
        name: pick.name,
        rate: pick.rate,
        unit: pick.unit ?? undefined,
        taxId: pick.taxId,
        hsnSac: pick.hsnSac ?? undefined,
      });
      return;
    }

    const item = items.data?.items.find((i) => i.id === value);
    if (!item) {
      updateLine(lineId, { itemId: null, pickedName: undefined });
      return;
    }
    updateLine(lineId, {
      itemId: item.id,
      pickedName: undefined,
      name: item.name,
      description: item.description ?? undefined,
      rate: item.rate ?? '',
      unit: item.unit ?? undefined,
      taxId: item.taxId,
      hsnSac: item.hsnSac ?? undefined,
    });
  };

  /** What the picker should read as - a billed pick has no id to show. */
  const pickValue = (line: EditorLine) =>
    line.itemId ?? (line.pickedName ? `${BILLED_PREFIX}${line.pickedName}` : '');

  const pickerOptions = (
    <>
      <option value="">Free text, or pick an item…</option>
      {items.data?.items.length ? (
        <optgroup label="Items">
          {items.data.items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </optgroup>
      ) : null}
      {billed.data?.items.length ? (
        <optgroup label="Billed before">
          {billed.data.items.map((b) => (
            <option key={b.name} value={`${BILLED_PREFIX}${b.name}`}>
              {b.name}
            </option>
          ))}
        </optgroup>
      ) : null}
    </>
  );
  const hasPicks = !!items.data?.items.length || !!billed.data?.items.length;

  // A foreign-currency document needs its rate on screen before it can be saved;
  // an issued invoice needs its reason.
  const canSave =
    !!customerId && lines.some((l) => l.name.trim() && l.rate !== '') && !save.isPending && !tagsBusy && fx.ready && (!editingIssued || reason.trim().length >= 3);

  return (
    <>
      <PageHeader
        title={editId ? `Edit ${existing.data?.number ?? 'draft'}` : config.title}
        subtitle={
          editId
            ? editingIssued
              ? `${existing.data?.number} has been issued. The change and your reason go on the record.`
              : existing.data?.externalNumber
                ? `Originally ${existing.data.externalNumber}. Saved as a draft; issue it from the Inbox or the invoice page.`
                : existing.data?.provisionalNumber === 1
                  ? `${existing.data.number} is a draft number. It becomes an invoice number when you issue it.`
                  : 'Saved as a draft.'
            : /**
               * What the header may promise depends on when the number is
               * taken. Where a draft is numbered separately, the next invoice
               * number is what THIS one gets only if it is issued next - so
               * the sentence says what is certain (issuing is what numbers it)
               * and shows the next number as what it is.
               */
              nextNumber.data?.number
              ? config.draftsAreNumberedSeparately
                ? `Issuing it takes the next invoice number, ${nextNumber.data.number} today. Saved as a draft it gets a draft number instead.`
                : `This will be ${nextNumber.data.number}.`
              : undefined
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => navigate(editId ? `/${config.path}/${editId}` : `/${config.path}`)} disabled={save.isPending}>
              Cancel
            </Button>
            {editId ? (
              <Button size="sm" disabled={!canSave || !(editingDraft || editingIssued)} loading={save.isPending} onClick={() => save.mutate(false)} data-testid="composer-save-draft">
                Save changes
              </Button>
            ) : (
              <>
                <Button variant="secondary" size="sm" disabled={!canSave} loading={save.isPending && save.variables === false} onClick={() => save.mutate(false)}>
                  Save draft
                </Button>
                <Button size="sm" disabled={!canSave} loading={save.isPending && save.variables === true} onClick={() => save.mutate(true)}>
                  {config.issueLabel}
                </Button>
              </>
            )}
          </>
        }
      />

      {editId && existing.data && (existing.data.status === 'void' || existing.data.status === 'written_off') ? (
        <p role="alert" className="mx-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive sm:mx-5">
          {existing.data.number} is {existing.data.status === 'void' ? 'void' : 'written off'} and is kept exactly as it was.
        </p>
      ) : null}
      {editingIssued ? (
        <div className="mx-4 rounded-md border border-amber-300/60 bg-amber-50 p-3 text-sm dark:border-amber-500/30 dark:bg-amber-500/10 sm:mx-5" data-testid="composer-reason">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            {existing.data?.number} has been issued - the customer may hold this figure.
          </p>
          <p className="mt-0.5 text-xs text-amber-900/80 dark:text-amber-200/80">
            Say why it is being changed. The reason is recorded with what changed - every figure before and after - and shows in the
            invoice&rsquo;s history and the activity log. If the customer has already paid, prefer a credit note.
          </p>
          <div className="mt-2">
            <Textarea
              label="Reason for the change"
              required
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Rate was entered tax-exclusive; the agreed price is ₹10,500 all-inclusive."
              error={error?.fieldError('reason')}
              data-testid="composer-reason-text"
            />
          </div>
        </div>
      ) : null}

      {addingCustomer ? (
        <CustomerSheet
          onClose={() => setAddingCustomer(false)}
          onSaved={async (created) => {
            // The list the picker reads is refetched before the pick, so the
            // option exists when the id is set.
            await queryClient.invalidateQueries({ queryKey: ['customers', org?.id] });
            setCustomerId(created.id);
            setAddingCustomer(false);
          }}
        />
      ) : null}
      {editingCustomer && customerId ? (
        <CustomerSheet customerId={customerId} onClose={() => setEditingCustomer(false)} onSaved={() => setEditingCustomer(false)} />
      ) : null}

      <div className="space-y-4 px-4 pb-10 sm:px-5">
        {error ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error.message}
          </p>
        ) : null}

        <Card>
          {/*
            `[&>*]:min-w-0` is load-bearing, not tidying.

            A CSS grid track defaults to `min-width: auto`, which means an item
            cannot shrink below its own content - and a <select> holding
            customer names is as wide as its longest option. On a phone that
            pushed every field past the right edge of this card and off the
            screen, so the customer picker and both date fields were partly
            unreachable. Letting the tracks shrink is what confines them.
          */}
          <CardContent className="grid gap-4 pt-5 [&>*]:min-w-0 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <span className="flex items-center text-sm font-medium">
                  <label htmlFor="customer">Customer</label>
                  <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>
                </span>
              <select
                id="customer"
                value={customerId}
                onChange={(e) => {
                  if (e.target.value === NEW_CUSTOMER) {
                    setAddingCustomer(true);
                    return;
                  }
                  setCustomerId(e.target.value);
                }}
                className="min-h-11 min-w-0 rounded-md border border-input bg-background px-3 text-base shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-9 sm:text-sm"
              >
                <option value="">Select a customer…</option>
                {draftCustomer ? (
                  <option value={draftCustomer.id}>{draftCustomer.displayName} (draft)</option>
                ) : null}
                {customers.data?.items.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
                {perms.can('customer', 'create') ? <option value={NEW_CUSTOMER}>+ Add a new customer…</option> : null}
              </select>
              {exportAddressMissing ? (
                <p role="status" className="flex items-start gap-1.5 text-xs text-warning" data-testid="export-address-warning">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                  <span>
                    An export invoice must show the customer&rsquo;s address and country of destination (CGST Rule 46).
                    Add them to the customer before sending.
                    {perms.can('customer', 'edit') ? (
                      <>
                        {' '}
                        <button type="button" className="font-medium underline underline-offset-2" onClick={() => setEditingCustomer(true)}>
                          Edit customer
                        </button>
                      </>
                    ) : null}
                  </span>
                </p>
              ) : null}
            </div>
            <Input label={config.dateLabel} type="date" value={date} onChange={(e) => setPickedDate(e.target.value)} />
            <Input
              label={config.secondDate.label}
              type="date"
              value={secondDate}
              onChange={(e) => setSecondDate(e.target.value)}
              hint={config.secondDate.hint}
            />
            <Input label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} hint="Their PO or contract number." />
            <CurrencySelect
              id="invoice-currency"
              value={currency}
              onChange={(code) => setPickedCurrency(code === (customer?.currency ?? baseCurrency) ? null : code)}
              base={baseCurrency}
              hint={
                currency === baseCurrency
                  ? undefined
                  : `Owed in ${currency}; the books keep it in ${baseCurrency} at the rate below.`
              }
            />
            {fx.foreign ? (
              <RateField
                id="invoice-exchange-rate"
                currency={currency}
                base={baseCurrency}
                rate={fx.rate}
                onChange={fx.setRate}
                source={fx.source}
                quote={fx.quote.data}
                loading={fx.quote.isLoading}
                typed={fx.typed}
                error={error?.fieldError('exchangeRate')}
              />
            ) : null}

            {/*
              Classification sits in the header block, beside the customer and
              the reference, because that is what it is: a fact about the whole
              document rather than a property of a line.

              The project list is filtered to the chosen customer's projects -
              an invoice and the project it is for have to be for the same
              customer, and an internal project may hold no document at all.
              `ProjectSelect` does that filtering so nobody has to meet the
              API's refusal to learn the rule.
            */}
            <ProjectSelect
              id="invoice-project"
              value={projectId}
              onChange={setProjectId}
              customerId={customerId || undefined}
              disabled={!customerId}
              hint={
                customerId
                  ? 'Which job this invoice is for.'
                  : 'Pick a customer first - a project belongs to one.'
              }
            />
            <IncomeCategorySelect
              id="invoice-income-category"
              value={incomeCategoryId}
              onChange={setIncomeCategoryId}
            />
            <TagPicker id="invoice-tags" value={tags} onChange={setTags} onBusyChange={setTagsBusy} />
          </CardContent>
        </Card>

        <Card>
          {/*
            ── THE LINE EDITOR, TWICE ──
            A seven-column table is right on a laptop and unusable on a phone:
            at 390px it showed the description and a sliver of Qty, with unit
            price, tax and the amount off the right edge. Raising an invoice is
            the single thing most likely to be done standing up, so the phone
            gets stacked cards instead.

            One or the other, never both - see `useIsPhone`. Rendering both
            would put two of every labelled input in the DOM.
          */}
          {isPhone ? (
            <ul className="divide-y divide-border">
              {lines.map((line, index) => {
                const computed = totals.lines.find((l) => l.id === line.id);
                return (
                  <li key={line.id} className="space-y-2 px-3 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Line {index + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Remove line"
                        disabled={lines.length === 1}
                        onClick={() => setLines((c) => c.filter((l) => l.id !== line.id))}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>

                    {hasPicks ? (
                      <select
                        value={pickValue(line)}
                        onChange={(e) => applyPick(line.id, e.target.value)}
                        aria-label="Choose an item"
                        className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background px-2 text-base sm:text-sm"
                      >
                        {pickerOptions}
                      </select>
                    ) : null}

                    <input
                      value={line.name}
                      onChange={(e) => updateLine(line.id, { name: e.target.value })}
                      placeholder="What are you billing for?"
                      aria-label="Line description"
                      className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />

                    {/* Qty and price side by side: both are short, and putting
                        them on one row keeps the whole line on one screen. */}
                    <div className="grid grid-cols-2 gap-2 [&>*]:min-w-0">
                      <label className="block text-xs text-muted-foreground">
                        Quantity
                        <input
                          type="number"
                          step="any"
                          value={line.quantity}
                          onChange={(e) => updateLine(line.id, { quantity: Number(e.target.value) })}
                          aria-label="Quantity"
                          className="mt-1 min-h-11 w-full min-w-0 rounded-md border border-input bg-background px-2 text-right text-base tabular focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </label>
                      <label className="block text-xs text-muted-foreground">
                        Unit price
                        <input
                          inputMode="decimal"
                          value={line.rate}
                          onChange={(e) => updateLine(line.id, { rate: e.target.value })}
                          placeholder="0.00"
                          aria-label="Unit price"
                          className="mt-1 min-h-11 w-full min-w-0 rounded-md border border-input bg-background px-2 text-right text-base tabular focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </label>
                    </div>

                    <select
                      value={line.taxId ?? ''}
                      onChange={(e) => updateLine(line.id, { taxId: e.target.value || null })}
                      aria-label="Tax rate"
                      className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background px-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="">No tax</option>
                      {taxes.data?.items.map((tax) => (
                        <option key={tax.id} value={tax.id}>
                          {tax.name}
                        </option>
                      ))}
                    </select>

                    {gstEnabled ? (
                      <HsnSacField
                        compact
                        label="HSN or SAC"
                        value={line.hsnSac ?? ''}
                        onChange={(next) => updateLine(line.id, { hsnSac: next })}
                        nameForSuggestion={line.name}
                      />
                    ) : null}

                    <div className="flex items-baseline justify-between border-t border-border pt-2 text-sm">
                      <span className="text-muted-foreground">Line total</span>
                      <span className="font-medium">
                        <Money money={computed?.net} value={0} currency={currency} />
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
          <div className="table-scroll">
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr>
                  <th className="w-8" />
                  <th className="h-9 px-3 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Item / description</th>
                  <th className="h-9 w-24 px-3 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Qty</th>
                  <th className="h-9 w-32 px-3 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Unit price</th>
                  <th className="h-9 w-40 px-3 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Tax</th>
                  <th className="h-9 w-32 px-3 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Amount</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  const computed = totals.lines.find((l) => l.id === line.id);
                  return (
                    <tr key={line.id} className="border-b border-border align-top">
                      <td className="px-1 pt-4 text-muted-foreground">
                        <GripVertical className="size-4" aria-hidden />
                      </td>
                      <td className="space-y-1.5 px-3 py-2">
                        {hasPicks ? (
                          <select
                            value={pickValue(line)}
                            onChange={(e) => applyPick(line.id, e.target.value)}
                            aria-label="Choose an item"
                            className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background sm:min-h-8 px-2 text-xs text-muted-foreground"
                          >
                            {pickerOptions}
                          </select>
                        ) : null}
                        <input
                          value={line.name}
                          onChange={(e) => updateLine(line.id, { name: e.target.value })}
                          placeholder="What are you billing for?"
                          aria-label="Line description"
                          className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background text-base sm:min-h-9 sm:text-sm px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                        {/*
                          HSN/SAC, only when GST is on.

                          The line has always carried it and the API has always
                          stored it - it just had no input, so the only way to
                          set one was to pick an item that had it. A line typed
                          straight onto an invoice could never get a code, and
                          GSTR-1 Table 12 is built per HSN.
                        */}
                        {gstEnabled ? (
                          <input
                            value={line.hsnSac ?? ''}
                            onChange={(e) => updateLine(line.id, { hsnSac: e.target.value })}
                            placeholder="HSN or SAC"
                            aria-label="HSN or SAC"
                            className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background sm:min-h-8 px-3 font-mono text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          />
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          step="any"
                          value={line.quantity}
                          onChange={(e) => updateLine(line.id, { quantity: Number(e.target.value) })}
                          aria-label="Quantity"
                          className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background text-base sm:min-h-9 sm:text-sm px-2 text-right tabular focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          inputMode="decimal"
                          value={line.rate}
                          onChange={(e) => updateLine(line.id, { rate: e.target.value })}
                          placeholder="0.00"
                          aria-label="Unit price"
                          className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background text-base sm:min-h-9 sm:text-sm px-2 text-right tabular focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={line.taxId ?? ''}
                          onChange={(e) => updateLine(line.id, { taxId: e.target.value || null })}
                          aria-label="Tax rate"
                          className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background text-base sm:min-h-9 sm:text-sm px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="">No tax</option>
                          {taxes.data?.items.map((tax) => (
                            <option key={tax.id} value={tax.id}>
                              {tax.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="inline-block pt-2">
                          <Money money={computed?.net} value={0} currency={currency} showSymbol={false} />
                        </span>
                      </td>
                      <td className="px-1 py-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Remove line"
                          // Never leave zero lines - an empty editor has no
                          // affordance to start again.
                          disabled={lines.length === 1}
                          onClick={() => setLines((c) => c.filter((l) => l.id !== line.id))}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          )}
          <div className="flex items-start justify-between gap-6 px-3 py-3">
            <Button variant="outline" size="sm" onClick={() => setLines((c) => [...c, blankLine()])}>
              <Plus className="size-4" aria-hidden />
              Add line
            </Button>

            <dl className="w-full max-w-xs space-y-1.5 text-sm">
              <Row label="Subtotal" value={<Money money={totals.subtotal} currency={currency} />} />
              {totals.taxBreakdown.map((component) => (
                <Row
                  key={`${component.name}-${component.percentage}`}
                  label={component.name}
                  value={<Money money={component.amount} currency={currency} />}
                  muted
                />
              ))}
              <Row
                label="Total"
                value={<Money money={totals.total} currency={currency} />}
                emphasis
              />
              {fx.foreign ? (
                <Row
                  label={`In ${baseCurrency}`}
                  value={
                    fx.parsed && fx.inBase(totals.total) ? (
                      <span data-testid="invoice-base-total">
                        <Money money={fx.inBase(totals.total) ?? undefined} currency={baseCurrency} /> at {formatRateHuman(fx.parsed)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">enter the rate</span>
                    )
                  }
                  muted
                />
              ) : null}
            </dl>
          </div>
        </Card>

        <Card>
          <CardContent className="space-y-4 pt-5">
            <Textarea
              label="Notes"
              value={notes}
              onChange={(e) => setTypedNotes(e.target.value)}
              placeholder="Anything the customer should see."
              error={error?.fieldError('notes')}
            />
            <Textarea
              label="Terms"
              value={terms}
              onChange={(e) => setTypedTerms(e.target.value)}
              placeholder="Payment within 15 days."
              error={error?.fieldError('terms')}
            />
            <p className="text-xs text-muted-foreground">
              Both start as the book&rsquo;s defaults from Settings &rarr; Letterhead and print at the foot of the document.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Row({
  label,
  value,
  muted,
  emphasis,
}: {
  label: string;
  value: React.ReactNode;
  muted?: boolean;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4',
        muted && 'text-muted-foreground',
        emphasis && 'border-t border-border pt-2 text-base font-semibold',
      )}
    >
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/**
 * The two entry points. Thin on purpose: the difference between an invoice and
 * a proforma is a table of five strings, not a second composer.
 */
export function InvoiceComposerPage() {
  return <DocumentComposer kind="invoice" />;
}

/** /invoices/:invoiceId/edit - the same composer, refilled from the draft. */
export function InvoiceEditPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  return <DocumentComposer kind="invoice" editId={invoiceId} />;
}

export function ProformaComposerPage() {
  return <DocumentComposer kind="proforma" />;
}
